export const EMPTY = "—";

/** A field value as a reviewer reads it: text as is, anything else as JSON. */
export const formatValue = (value: unknown): string => {
  const isEmpty = value === null || value === undefined || value === "";
  if (isEmpty) return EMPTY;
  return typeof value === "string" ? value : JSON.stringify(value);
};

/** A value a reviewer typed: JSON when it parses, else the trimmed text, null when blank. */
export const parseTypedValue = (text: string): unknown => {
  const trimmed = text.trim();
  if (trimmed === "") return null;
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return trimmed;
  }
};

/**
 * A value as a reviewer would type it, so parseTypedValue reads it back unchanged:
 * text as is unless it would read as JSON, anything else as JSON.
 */
export const toTypedText = (value: unknown): string => {
  if (typeof value !== "string") return JSON.stringify(value ?? null);
  return parseTypedValue(value) === value ? value : JSON.stringify(value);
};

/**
 * Whether the golden service counts a value as empty: null, an empty string, an empty
 * list, or an empty object (registry.is_empty). It stores no correction to an empty
 * value.
 */
export const isEmptyValue = (value: unknown): boolean => {
  if (value === null || value === undefined || value === "") return true;
  if (Array.isArray(value)) return value.length === 0;
  return typeof value === "object" && Object.keys(value).length === 0;
};

export const CORRECT_VALUE_REQUIRED =
  "Enter the correct value. An empty value can't be stored as a correction.";

/** A timestamp in the reader's locale, or a dash when there is none. */
export const formatTimestamp = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString() : EMPTY;

/** A share as a percentage to one decimal, or a dash when there is none. */
export const percent = (share: number | null | undefined): string =>
  share === null || share === undefined
    ? EMPTY
    : `${(share * 100).toFixed(1)}%`;
